import { Directive, DoCheck, ElementRef, EventEmitter, HostListener, Input, OnDestroy, Output } from '@angular/core';

import { buildBodyIndex, findQuoteOffsets, QuoteOffsets, rangeForOffsets } from '../../utils/email-body-anchor.util';

/**
 * One line's anchor in the body: the quote to find, plus the opaque key of the list row it maps back
 * to. Deliberately narrower than `ISourceLocationTextRange` — no kind / document_ref / confidence —
 * so the panel maps at the boundary and this stays usable from `shared/` later. The text
 * counterpart of `DocumentLineRegion` / `DocumentCellRegion`.
 */
export interface EmailBodyAnchorRegion {
  key: string;
  quote: string;
  occurrence?: number;
}

const MARK_CLASS = 'email-anchor';
const KEY_ATTR = 'data-anchor-key';

/**
 * Highlights order lines' `kind: 'text_range'` anchors in the rendered email body, in BOTH
 * directions (WB-7625).
 *
 * EVERY anchored line is wrapped, not just the active one: the browser can only report "the pointer
 * is over line 3's text" if that text is its own element. The marks are transparent hit targets
 * until made active — the same arrangement as the box path's hotspot strips.
 *
 * The host is an `[innerHTML]` body, so this mutates DOM Angular never diffs. Safe in one direction
 * only: Angular rewrites that subtree wholesale when the bound string's reference changes, which
 * silently takes the marks with it — see the staleness check in `ngDoCheck`.
 *
 * Latent coupling worth knowing: the order views bind `email.content` raw, with no quote/forward
 * splitting. If splitting is ever added there, quotes stop existing verbatim in the rendered DOM
 * and anchors go quiet rather than break loudly.
 */
@Directive({
  selector: '[appEmailBodyHighlight]',
})
export class EmailBodyHighlightDirective implements DoCheck, OnDestroy {
  /** Every anchored line, not only the hovered one — each needs its own hit target. */
  @Input('appEmailBodyHighlight') anchors: EmailBodyAnchorRegion[] | null = null;
  /** The line to emphasize. A class toggle, NEVER a reason to re-resolve — see `ngDoCheck`. */
  @Input() activeAnchorKey: string | null = null;
  /** Pinned while a re-match editor is open (WB-7483): a deeper fill, matching `.hl-box--locked`. */
  @Input() anchorLocked = false;

  /** Reverse hover: the anchored line under the pointer, or null on leave. */
  @Output() anchorHover = new EventEmitter<string | null>();

  /** Every mark currently in the body, across all anchors. One line is often several marks. */
  private marks: HTMLElement[] = [];

  /**
   * Identity of the resolved anchor SET. The host binds a getter, so the inputs are fresh objects
   * every change-detection cycle; without this gate the body would be re-wrapped on every tick.
   * Same pattern as `applyCellHighlight` / `highlightScrollSig`.
   */
  private signature: string | null = null;
  /** Last painted tier, so a class toggle is skipped when nothing moved. */
  private appliedActive: string | null = null;
  private appliedLocked = false;
  /** Last key emitted, so sliding across one line's several marks emits once. */
  private hoveredKey: string | null = null;
  /** True when the active key came from OUR hover — see `emitHover`. */
  private selfHovered = false;

  constructor(private readonly el: ElementRef<HTMLElement>) {}

  /**
   * `DoCheck` rather than `OnChanges`: the signature gate makes them equivalent for the normal
   * path, and only this one also fires when Angular rewrites the `[innerHTML]` body WITHOUT an
   * input changing, which silently detaches every mark.
   *
   * The two halves are deliberately separate. Re-resolving is expensive and mutates the DOM;
   * changing which line is active must therefore cost only a class toggle. If the active key fed
   * the signature, the mark under the cursor would be destroyed and recreated on every hover —
   * `mouseout` clears the key, that re-wraps, the pointer lands on a new element, `mouseover` fires
   * again: a flicker loop that never settles.
   */
  ngDoCheck(): void {
    const signature = this.anchorSignature();
    const stale = this.marks.length > 0 && !this.marks[0].isConnected;
    if (stale || signature !== this.signature) {
      this.signature = signature;
      this.resolve();
      this.appliedActive = null; // a fresh wrap carries no tier classes yet
      this.appliedLocked = false;
    }
    if (this.activeAnchorKey !== this.appliedActive || this.anchorLocked !== this.appliedLocked) {
      this.appliedActive = this.activeAnchorKey;
      this.appliedLocked = this.anchorLocked;
      this.paintActive();
      if (!this.selfHovered) {
        this.scrollAnchorIntoView();
      }
    }
    this.selfHovered = false;
  }

  ngOnDestroy(): void {
    this.clear();
  }

  /**
   * Delegated, like the spreadsheet path's `<tbody>` handler: `mouseover` bubbles where
   * `mouseenter` does not, so this is two listeners for the whole body instead of two per mark.
   */
  @HostListener('mouseover', ['$event'])
  onHostMouseOver(event: Event): void {
    if (!this.marks.length) {
      return;
    }
    const target = event.target as HTMLElement | null;
    const mark = target?.closest ? (target.closest(`mark.${MARK_CLASS}`) as HTMLElement | null) : null;
    this.emitHover(mark?.getAttribute(KEY_ATTR) ?? null);
  }

  /** `mouseleave` does not bubble, so on the host it fires exactly once on a real exit. */
  @HostListener('mouseleave')
  onHostMouseLeave(): void {
    this.emitHover(null);
  }

  /**
   * Scroll the active line's anchor into view. Public so the host can call it once the body's
   * expand animation has finished — scrolling into a box that is still 0-high does nothing, and the
   * animation's `done` event is the only deterministic moment it has.
   */
  scrollAnchorIntoView(): void {
    const key = this.activeAnchorKey;
    if (!key) {
      return;
    }
    const mark = this.marks.find((m) => m.getAttribute(KEY_ATTR) === key);
    if (mark?.isConnected && mark.getClientRects().length) {
      mark.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }

  private anchorSignature(): string | null {
    const regions = (this.anchors || []).filter((a) => a?.key && a.quote);
    return regions.length ? regions.map((a) => `${a.key}|${a.occurrence ?? 1}|${a.quote}`).join(';') : null;
  }

  /** Deduped: `mouseover` re-fires for every element crossed, and each emit costs the host a CD pass. */
  private emitHover(key: string | null): void {
    if (key === this.hoveredKey) {
      return;
    }
    this.hoveredKey = key;
    // The pointer is already on this text, so the active-key change it is about to cause must NOT
    // scroll: that would move the text out from under the cursor and could change what is hovered.
    this.selfHovered = true;
    this.anchorHover.emit(key);
  }

  /** Class-only update. Never re-wraps — see the `ngDoCheck` comment. */
  private paintActive(): void {
    const active = this.activeAnchorKey;
    this.marks.forEach((mark) => {
      const isActive = !!active && mark.getAttribute(KEY_ATTR) === active;
      mark.classList.toggle(`${MARK_CLASS}--active`, isActive);
      mark.classList.toggle(`${MARK_CLASS}--locked`, isActive && this.anchorLocked);
    });
  }

  private resolve(): void {
    this.clear();
    const regions = (this.anchors || []).filter((a) => a?.key && a.quote);
    if (!regions.length) {
      return;
    }

    // ONE walk for every anchor, and necessarily before any wrapping: the walk reads the DOM that
    // wrapping is about to mutate.
    const index = buildBodyIndex(this.el.nativeElement);
    if (!index.text) {
      return;
    }

    const located: Array<{ key: string; offsets: QuoteOffsets }> = [];
    regions.forEach((region) => {
      const offsets = findQuoteOffsets(index, region.quote, region.occurrence);
      if (offsets) {
        located.push({ key: region.key, offsets }); // an unresolved anchor just gets no hit target
      }
    });

    // Drop overlaps. One line's quote can be a substring of another's, and wrapping both would
    // corrupt the DOM — so the earlier hit wins and the later one silently gets no target. A
    // missing hit target is recoverable; a mangled body is not.
    located.sort((a, b) => a.offsets.start - b.offsets.start);
    const kept: Array<{ key: string; offsets: QuoteOffsets }> = [];
    let usedTo = 0;
    located.forEach((item) => {
      if (item.offsets.start >= usedTo) {
        kept.push(item);
        usedTo = item.offsets.end;
      }
    });

    // Capture every anchor's DOM position BEFORE mutating, then wrap BACK-TO-FRONT. `splitText`
    // keeps the ORIGINAL node as the head, so wrapping a later range never invalidates an earlier
    // one's already-captured (node, offset). Front-to-back silently shifts every anchor after the
    // first — and only when two of them share a text node, which is why it would survive review.
    const ranges: Array<{ key: string; range: Range }> = [];
    kept.forEach((item) => {
      const range = rangeForOffsets(index, item.offsets);
      if (range) {
        ranges.push({ key: item.key, range });
      }
    });

    const wrapped: HTMLElement[][] = [];
    wrapped.length = ranges.length;
    for (let i = ranges.length - 1; i >= 0; i -= 1) {
      wrapped[i] = wrapRange(ranges[i].range, ranges[i].key);
    }
    this.marks = ([] as HTMLElement[]).concat(...wrapped);
  }

  /** Unwrap every mark and re-merge the text nodes the wrap split. */
  private clear(): void {
    const parents = new Set<Node>();
    this.marks.forEach((mark) => {
      const parent = mark.parentNode;
      if (!parent) {
        return;
      }
      while (mark.firstChild) {
        parent.insertBefore(mark.firstChild, mark);
      }
      parent.removeChild(mark);
      parents.add(parent);
    });
    this.marks = [];
    // The pointer may still be over where a mark was, so let the next mouseover re-emit.
    this.hoveredKey = null;
    // Without this, every hover cycle leaves the body a little more fragmented. Scoped to the
    // affected parents: normalizing the whole body would churn node identity nothing touched.
    parents.forEach((parent) => parent.normalize());
  }
}

/**
 * Wrap `range` in one `<mark>` per text node it covers, tagged with the line key.
 *
 * NOT `Range.surroundContents()`, which throws `InvalidStateError` when the range partially selects
 * a non-Text node — the normal case here, since a quote is the whole logical row and regularly
 * spans two `<br>`-separated fragments. Splitting per text node is also what makes the fill render
 * correctly across a line break instead of as one box over both.
 */
function wrapRange(range: Range, key: string): HTMLElement[] {
  const doc = range.startContainer.ownerDocument;
  if (!doc) {
    return [];
  }

  // `buildRange` always anchors on Text nodes, so the common ancestor is either one of them or an
  // element containing both.
  const root = range.commonAncestorContainer;
  const candidates: Text[] = [];
  if (root.nodeType === Node.TEXT_NODE) {
    candidates.push(root as Text);
  } else {
    const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      candidates.push(node as Text);
    }
  }

  // Collect the pieces BEFORE mutating: splitText inserts siblings, which a walker in flight would
  // then visit. Bounded by the range's own containers rather than `intersectsNode`, which also
  // reports nodes that merely touch a boundary.
  const pieces: Array<{ node: Text; start: number; end: number }> = [];
  let started = false;
  for (const node of candidates) {
    if (!started && node !== range.startContainer) {
      continue;
    }
    started = true;
    const start = node === range.startContainer ? range.startOffset : 0;
    const end = node === range.endContainer ? range.endOffset : node.data.length;
    if (end > start) {
      pieces.push({ node, start, end });
    }
    if (node === range.endContainer) {
      break;
    }
  }

  return pieces.map(({ node, start, end }) => {
    // Tail first: splitting the head would shift `end`.
    if (end < node.data.length) {
      node.splitText(end);
    }
    const target = start > 0 ? node.splitText(start) : node;
    const mark = doc.createElement('mark');
    mark.className = MARK_CLASS;
    mark.setAttribute(KEY_ATTR, key);
    target.parentNode?.insertBefore(mark, target);
    mark.appendChild(target);
    return mark;
  });
}
